import axios from "axios";
import buildUrl from "build-url-ts";
import { nanoid } from "nanoid";

const apiUrl = process.env.REACT_APP_CLEVERSTORY_PYTHON_SERVICE_URL;
const PROMPT_EDITOR_URL = `${apiUrl}/ai-builder`;

export interface PromptSectionRequest {
  prompt: string;
}

export interface PageStructureSection {
  id?: string;
  type?: string;
  content?: Record<string, any>;
  [key: string]: any;
}

export interface PageStructure {
  sections?: PageStructureSection[];
  [key: string]: any;
}

export interface PromptSectionResponse {
  storyboardId: string;
  pageId: string;
  prompt: string;
  session: string;
  answer: string | PageStructure;
  storyboardName: string;
  pageName: string;
}

export interface ParsedPromptSectionData {
  storyboardId: string;
  pageId: string;
  prompt: string;
  session: string;
  pageStructure: PageStructure;
  storyboardName: string;
  pageName: string;
}

const parseAnswer = (answer: string | PageStructure): PageStructure => {
  let parsed: PageStructure;
  try {
    parsed =
      typeof answer === "string" ? JSON.parse(answer) : answer;
  } catch {
    return answer as PageStructure;
  }
  return parsed?.page_structure ?? parsed;
};

const addIdsToSections = (
  sections: PageStructureSection[]
): PageStructureSection[] => {
  if (!Array.isArray(sections)) return sections;
  return sections.map((section) => ({
    ...section,
    id: section.id ?? nanoid(6),
  }));
};

export const promptEditorApi = {
  generateSection: async (
    request: PromptSectionRequest,
    workspaceId: string
  ): Promise<ParsedPromptSectionData> => {
    const url = buildUrl(`${PROMPT_EDITOR_URL}/section`, {});

    const response = await axios.post<PromptSectionResponse>(url, request, {
      headers: { "CS-WORKSPACE-ID": workspaceId },
    });

    const raw = response.data;
    const pageStructure = parseAnswer(raw.answer);

    if (Array.isArray(pageStructure?.sections)) {
      pageStructure.sections = addIdsToSections(pageStructure.sections);
    }

    return {
      storyboardId: raw.storyboardId,
      pageId: raw.pageId,
      prompt: raw.prompt,
      session: raw.session,
      pageStructure,
      storyboardName: raw.storyboardName,
      pageName: raw.pageName,
    };
  },
};